import React, { useMemo, useState } from "react";
import { HomeWork as TypeIcon } from "@mui/icons-material";

import ActionsDropdown from "../../../../../../components/Dropdown/ActionsDropdown";
import { KebabButton } from "../../../../../../components/KebabButton";
import { LiabilityActionSheet } from "../LiabilityActionSheet";
import {
  capitalizeFirstLetter,
  formatDate,
  formatCurrency,
  formatPercent,
  DateFormat,
} from "../../../../../../utils";
import { getStatusBadge, getTermInfo } from "../helpers";
import { LiabilitySummaryProps } from "./types";
import {
  SummaryCard,
  SummaryHeader,
  KebabWrapper,
  ActionsWrapper,
  IdentityBlock,
  TitleRow,
  HeroName,
  BadgeRow,
  StatusBadge,
  TypeBadge,
  LastPaymentText,
  LastPaymentPrefixLong,
  LastPaymentPrefixShort,
  BalanceBlock,
  BalanceLabel,
  BalanceValue,
  ProgressBlock,
  ProgressMeta,
  ProgressPaid,
  ProgressAmounts,
  ProgressToGo,
  ProgressTrack,
  ProgressFill,
  DetailsPanel,
  DetailBlock,
  DetailLabel,
  DetailValue,
  DetailSub,
} from "./styles";
import { currentRateOf } from "../../../../helpers";

export const LiabilitySummary: React.FC<LiabilitySummaryProps> = React.memo(
  ({ liability, lastPaymentDate, dropdownActions }) => {
    const [menuOpen, setMenuOpen] = useState(false);

    const currentRate = currentRateOf(liability.interestRateHistory);
    const interestRate = currentRate?.interestRate ?? 0;

    const statusBadge = getStatusBadge(liability.status, liability.isActive);
    const termInfo = useMemo(
      () => getTermInfo(liability.startDate, liability.endDate),
      [liability.startDate, liability.endDate],
    );

    const amountLent = liability.amountLent ?? 0;
    const principalRepaid = amountLent - liability.currentAmount;
    const paidOffPercent =
      amountLent > 0
        ? Math.min(100, Math.max(0, (principalRepaid / amountLent) * 100))
        : 0;
    const toGoPercent = 100 - paidOffPercent;

    const formattedLastPaymentDate = lastPaymentDate
      ? formatDate(lastPaymentDate, DateFormat.DMY_SPACE)
      : null;
    const sheetSubtitle = formattedLastPaymentDate
      ? `Last payment ${formattedLastPaymentDate}`
      : "No payments yet";
    const rateEffectiveLabel = currentRate?.effectiveDate
      ? `Since ${formatDate(currentRate.effectiveDate, DateFormat.DMY_SPACE)}`
      : null;

    return (
      <SummaryCard>
        <SummaryHeader>
          <IdentityBlock>
            <TitleRow>
              <HeroName>{liability.name}</HeroName>
              <BadgeRow>
                <StatusBadge $variant={statusBadge.variant}>
                  {statusBadge.label}
                </StatusBadge>
                <TypeBadge>
                  <TypeIcon />
                  {liability.liabilityTypeName}
                </TypeBadge>
              </BadgeRow>
            </TitleRow>
            {formattedLastPaymentDate && (
              <LastPaymentText>
                <LastPaymentPrefixLong>Last payment </LastPaymentPrefixLong>
                <LastPaymentPrefixShort>Paid </LastPaymentPrefixShort>
                {formattedLastPaymentDate}
              </LastPaymentText>
            )}
          </IdentityBlock>

          <KebabWrapper>
            <KebabButton
              aria-label="Liability actions"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
            />
          </KebabWrapper>
          <ActionsWrapper>
            <ActionsDropdown dropdownActions={dropdownActions} />
          </ActionsWrapper>
        </SummaryHeader>

        <LiabilityActionSheet
          name={liability.name}
          subtitle={sheetSubtitle}
          isOpen={menuOpen}
          dropdownActions={dropdownActions}
          onClose={() => setMenuOpen(false)}
        />

        <BalanceBlock>
          <BalanceLabel>Outstanding Balance</BalanceLabel>
          <BalanceValue data-testid="liability-current-amount">
            {formatCurrency(liability.currentAmount)}
          </BalanceValue>
        </BalanceBlock>

        <ProgressBlock>
          <ProgressMeta>
            <ProgressPaid data-testid="liability-paid-off">
              {formatPercent(paidOffPercent, 1)} paid off
            </ProgressPaid>
            <ProgressAmounts>
              {formatCurrency(principalRepaid)} of {formatCurrency(amountLent)}
            </ProgressAmounts>
            <ProgressToGo>{formatPercent(toGoPercent, 1)} to go</ProgressToGo>
          </ProgressMeta>
          <ProgressTrack
            role="progressbar"
            aria-valuenow={Math.round(paidOffPercent)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <ProgressFill $pct={paidOffPercent} />
          </ProgressTrack>
        </ProgressBlock>

        <DetailsPanel>
          <DetailBlock>
            <DetailLabel>Institution</DetailLabel>
            <DetailValue data-testid="liability-institution">
              {liability.institution ?? "Not specified"}
            </DetailValue>
          </DetailBlock>

          <DetailBlock>
            <DetailLabel>Interest Rate</DetailLabel>
            <DetailValue $accent data-testid="liability-interest-rate">
              {interestRate.toFixed(3)}%
            </DetailValue>
            {rateEffectiveLabel && <DetailSub>{rateEffectiveLabel}</DetailSub>}
          </DetailBlock>

          <DetailBlock>
            <DetailLabel>Rate Type</DetailLabel>
            <DetailValue data-testid="liability-rate-type">
              {currentRate ? capitalizeFirstLetter(currentRate.rateType) : "—"}
            </DetailValue>
          </DetailBlock>

          {termInfo && (
            <DetailBlock>
              <DetailLabel>Term</DetailLabel>
              <DetailValue data-testid="liability-term">
                {termInfo.totalLabel}
              </DetailValue>
              <DetailSub>{termInfo.remainingLabel}</DetailSub>
            </DetailBlock>
          )}

          <DetailBlock>
            <DetailLabel>Started</DetailLabel>
            <DetailValue data-testid="liability-start-date">
              {formatDate(liability.startDate, DateFormat.DMY_SPACE)}
            </DetailValue>
          </DetailBlock>

          <DetailBlock>
            <DetailLabel>Debt-free</DetailLabel>
            <DetailValue data-testid="liability-end-date">
              {formatDate(liability.endDate, DateFormat.DMY_SPACE)}
            </DetailValue>
          </DetailBlock>
        </DetailsPanel>
      </SummaryCard>
    );
  },
);

LiabilitySummary.displayName = "LiabilitySummary";
